import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Star, MapPin, ChefHat, Trash2, Save, Share2, Users, Link as LinkIcon, Camera, X } from "lucide-react";
import { compressImage } from "@/utils/image";

const VisitDetailDialog = ({ open, onOpenChange, visit, onUpdated }) => {
  const [restaurant, setRestaurant] = useState(null);
  const [visitDate, setVisitDate] = useState("");
  const [visitCount, setVisitCount] = useState(1);
  const [notes, setNotes] = useState("");
  const [cost, setCost] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    if (!visit) return;
    setVisitDate((visit.visit_date || "").split("T")[0]);
    setVisitCount(visit.visit_count || 1);
    setNotes(visit.notes || "");
    setCost(visit.cost != null ? String(visit.cost) : "");
    setCurrency(visit.currency || "EUR");
    setConfirmDelete(false);
    setPhotos(visit.photos || []);
    setRestaurant(null);
    axios.get(`${API}/restaurants/${visit.restaurant_id}`, { withCredentials: true })
      .then(res => setRestaurant(res.data))
      .catch(() => setRestaurant(null));
  }, [visit]);

  if (!visit) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/visits/${visit.visit_id}`, {
        visit_date: visitDate,
        visit_count: visitCount,
        notes: notes || null,
        photos,
        cost: cost ? parseFloat(cost) : null,
        currency
      }, { withCredentials: true });
      toast.success("Besuch aktualisiert");
      onOpenChange(false);
      onUpdated?.();
    } catch (error) {
      toast.error("Aktualisierung fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    try {
      await axios.delete(`${API}/visits/${visit.visit_id}`, { withCredentials: true });
      toast.success("Besuch gelöscht");
      onOpenChange(false);
      onUpdated?.();
    } catch (error) {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const dataUrl = await compressImage(file);
      const response = await axios.post(`${API}/upload/photo`, { photo: dataUrl }, { withCredentials: true });
      const newPhotos = [...photos, response.data.photo_id];
      setPhotos(newPhotos);
      await axios.put(`${API}/visits/${visit.visit_id}`, { photos: newPhotos }, { withCredentials: true });
      toast.success("Foto hinzugefügt!");
      onUpdated?.();
    } catch (error) {
      toast.error("Foto-Upload fehlgeschlagen");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = async (photoId) => {
    const newPhotos = photos.filter(id => id !== photoId);
    setPhotos(newPhotos);
    try {
      await axios.put(`${API}/visits/${visit.visit_id}`, { photos: newPhotos }, { withCredentials: true });
      axios.delete(`${API}/upload/photo/${photoId}`, { withCredentials: true }).catch(() => {});
      toast.success("Foto entfernt");
      onUpdated?.();
    } catch (error) {
      toast.error("Entfernen fehlgeschlagen");
    }
  };

  const buildShareText = () => {
    const starsTxt = "⭐".repeat(visit.stars);
    const loc = restaurant ? ` in ${restaurant.location || restaurant.city}, ${restaurant.country}` : "";
    const dateTxt = visitDate ? ` am ${new Date(visitDate).toLocaleDateString("de-DE")}` : "";
    return `Ich war${dateTxt} im ${visit.restaurant_name} ${starsTxt}${loc} – getrackt mit Étoile ✨`;
  };

  const handleShareCommunity = async () => {
    setSharing(true);
    try {
      await axios.post(`${API}/community/posts`, { content: buildShareText() }, { withCredentials: true });
      toast.success("In der Community geteilt!");
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Bitte aktiviere zuerst die Community-Funktion in deinem Profil");
      } else {
        toast.error("Teilen fehlgeschlagen");
      }
    } finally {
      setSharing(false);
    }
  };

  const handleShareLink = async () => {
    setSharing(true);
    try {
      const response = await axios.post(`${API}/visits/${visit.visit_id}/share`, {}, { withCredentials: true });
      const link = `${window.location.origin}/visits/shared/${response.data.share_code}`;
      await navigator.clipboard.writeText(link);
      toast.success("Öffentlicher Link kopiert!");
    } catch (error) {
      toast.error("Link konnte nicht erstellt werden");
    } finally {
      setSharing(false);
    }
  };

  const handleShare = async () => {
    const text = buildShareText();
    if (navigator.share) {
      try {
        await navigator.share({ text });
      } catch (error) {
        if (error?.name !== "AbortError") {
          console.error("Share failed:", error);
        }
      }
    } else {
      try {
        await navigator.clipboard.writeText(text);
        toast.success("Text in die Zwischenablage kopiert");
      } catch (error) {
        toast.error("Teilen nicht möglich");
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md max-h-[90vh] overflow-y-auto" data-testid="visit-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ChefHat className="w-5 h-5 text-primary" />
            Besuchsdetails
          </DialogTitle>
        </DialogHeader>

        {/* Restaurant Info */}
        <div className="p-4 bg-muted/30 rounded-lg" data-testid="visit-detail-restaurant-info">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-lg">{visit.restaurant_name}</h3>
            <div className="flex gap-0.5">
              {[...Array(visit.stars)].map((_, i) => (
                <Star key={`detail-star-${i}`} className="w-4 h-4 text-primary fill-primary" />
              ))}
            </div>
          </div>
          {restaurant && (
            <>
              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                <MapPin className="w-3 h-3" />
                {restaurant.location || restaurant.city}, {restaurant.country}
              </p>
              {restaurant.cuisine_type && (
                <p className="text-xs text-muted-foreground mt-1">Küche: {restaurant.cuisine_type}</p>
              )}
            </>
          )}
        </div>

        <div className="space-y-4">
          {/* Visit Date */}
          <div>
            <Label className="text-sm text-muted-foreground mb-2 block">Besuchsdatum</Label>
            <Input
              type="date"
              value={visitDate}
              onChange={(e) => setVisitDate(e.target.value)}
              max={new Date().toISOString().split("T")[0]}
              className="bg-transparent border-border focus:border-primary"
              data-testid="visit-detail-date"
            />
          </div>

          {/* Visit Count */}
          <div>
            <Label className="text-sm text-muted-foreground mb-2 block">Anzahl der Besuche</Label>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setVisitCount(Math.max(1, visitCount - 1))}
                className="border-border"
                data-testid="visit-detail-count-decrease"
              >
                -
              </Button>
              <span className="text-xl font-semibold w-8 text-center" data-testid="visit-detail-count">{visitCount}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setVisitCount(visitCount + 1)}
                className="border-border"
                data-testid="visit-detail-count-increase"
              >
                +
              </Button>
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label className="text-sm text-muted-foreground mb-2 block">Notizen</Label>
            <Textarea
              placeholder="Deine Eindrücke, Lieblingsgerichte..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="bg-transparent border-border focus:border-primary"
              data-testid="visit-detail-notes"
            />
          </div>

          {/* Cost */}
          <div>
            <Label className="text-sm text-muted-foreground mb-2 block">Kosten (optional)</Label>
            <div className="flex gap-2">
              <Input
                type="number"
                placeholder="0.00"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                className="flex-1 bg-transparent border-border focus:border-primary"
                data-testid="visit-detail-cost"
              />
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="px-3 py-2 bg-card border border-border rounded-md text-sm"
                data-testid="visit-detail-currency"
              >
                <option value="EUR">€ EUR</option>
                <option value="USD">$ USD</option>
              </select>
            </div>
          </div>

          {/* Photos */}
          <div>
            <Label className="text-sm text-muted-foreground mb-2 block">Fotos</Label>
            <div className="flex gap-2 flex-wrap" data-testid="visit-detail-photos">
              {photos.map((photoId) => (
                <div key={photoId} className="relative group">
                  <img
                    src={`${API}/upload/photo/${photoId}`}
                    alt=""
                    className="w-16 h-16 object-cover rounded border border-border"
                  />
                  <button
                    onClick={() => removePhoto(photoId)}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-destructive text-white rounded-full flex items-center justify-center opacity-80 hover:opacity-100"
                    data-testid={`detail-remove-photo-${photoId}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <label className="w-16 h-16 border border-dashed border-border rounded flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors">
                <Camera className="w-4 h-4 text-muted-foreground" />
                <span className="text-[10px] text-muted-foreground mt-0.5">{uploadingPhoto ? "..." : "Foto"}</span>
                <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" disabled={uploadingPhoto} data-testid="visit-detail-photo-input" />
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="pt-4 border-t border-border space-y-3">
            <Label className="text-sm text-muted-foreground block">Besuch teilen</Label>
            <div className="grid grid-cols-3 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleShareCommunity}
                disabled={sharing}
                className="border-border hover:border-primary hover:text-primary flex-col h-auto py-2 gap-1"
                data-testid="visit-share-community"
              >
                <Users className="w-4 h-4" />
                <span className="text-xs">Community</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleShareLink}
                disabled={sharing}
                className="border-border hover:border-primary hover:text-primary flex-col h-auto py-2 gap-1"
                data-testid="visit-share-link"
              >
                <LinkIcon className="w-4 h-4" />
                <span className="text-xs">Öffentl. Link</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleShare}
                className="border-border hover:border-primary hover:text-primary flex-col h-auto py-2 gap-1"
                data-testid="visit-detail-share"
              >
                <Share2 className="w-4 h-4" />
                <span className="text-xs">Apps</span>
              </Button>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleSave}
                disabled={saving || !visitDate}
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="visit-detail-save"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Speichern..." : "Speichern"}
              </Button>
              <Button
                variant="outline"
                onClick={handleDelete}
                className="flex-1 border-destructive/40 text-destructive hover:bg-destructive/10"
                data-testid="visit-detail-delete"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {confirmDelete ? "Wirklich löschen?" : "Besuch löschen"}
              </Button>
            </div>
            {confirmDelete && (
              <p className="text-xs text-muted-foreground text-center">
                Erneut klicken, um den Besuch endgültig zu löschen. Die Sterne werden abgezogen.
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VisitDetailDialog;
